import React, { useEffect, useRef, useState } from "react";
import { Download, Loader2, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { API, apiClient, formatApiError } from "@/lib/auth";
import { KindIcon, fmtBytes } from "@/pages/admin/documents/kindMeta";
import { inputCls } from "./FormRenderer";

export const downloadDoc = (id) => {
  const token = localStorage.getItem("nex_token");
  window.open(`${API}/documents/${id}/download?auth=${encodeURIComponent(token)}`, "_blank", "noopener");
};

function query(docs, search) {
  const p = new URLSearchParams();
  if (docs?.mode === "picked") p.set("ids", (docs.ids || []).join(","));
  else if (docs?.folder) p.set("folder", docs.folder);
  if (search) p.set("search", search);
  return `/documents?${p}`;
}

// Card popup panel: the card's folder (or hand-picked files) with download + upload-into-folder.
export default function DocLibrary({ docs }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const canUpload = docs?.mode !== "picked";

  const load = () => apiClient.get(query(docs, search)).then(({ data }) => setItems(data.items || data.documents || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, [search, docs?.mode, docs?.folder, (docs?.ids || []).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", docs?.folder || "");
        await apiClient.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div data-testid="ops-doc-library" className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${docs?.folder || "documents"}…`} data-testid="ops-doc-search" className={`${inputCls} pl-8`} />
        </div>
        {canUpload && (
          <>
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => upload([...e.target.files])} data-testid="ops-doc-upload-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ops-doc-upload"
              className="inline-flex items-center gap-1.5 px-3 rounded-md bg-gradient-to-r from-brand-blue to-brand-cyan text-navy-950 font-mono text-[0.6rem] font-bold tracking-[0.14em] uppercase hover:brightness-110 disabled:opacity-60">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
            </button>
          </>
        )}
      </div>
      {docs?.folder && canUpload && <div className="font-mono text-[0.55rem] tracking-[0.18em] uppercase text-slate-500">Folder · {docs.folder}</div>}
      <ul className="space-y-1.5">
        {items.map((d) => (
          <li key={d.id} data-testid={`ops-doc-row-${d.id}`} className="flex items-center gap-3 rounded-md border border-slate-800 bg-navy-950/60 px-3 py-2">
            <KindIcon kind={d.kind} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-100 truncate">{d.original_filename}</div>
              <div className="text-[0.65rem] text-slate-500">{fmtBytes(d.size)} · {new Date(d.created_at).toLocaleDateString()}</div>
            </div>
            <button type="button" onClick={() => downloadDoc(d.id)} data-testid={`ops-doc-download-${d.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-brand-cyan/50 text-brand-cyan hover:bg-brand-cyan/10 font-mono text-[0.55rem] tracking-[0.14em] uppercase transition-colors">
              <Download className="w-3 h-3" /> Download
            </button>
          </li>
        ))}
      </ul>
      {!items.length && <div data-testid="ops-doc-empty" className="p-6 text-center text-sm text-slate-500 rounded-md border border-dashed border-slate-800">{canUpload ? "No documents here yet." : "No hand-picked files available — pick files in Configure → Quick forms."}</div>}
    </div>
  );
}
